import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { Wrench, CircleCheck, ShieldX } from 'lucide-react';
import { Vehicle } from '../../../../../shared/types/vehicle.types';
import { VehicleService } from '../../../../../shared/types/vehicle-service.types';
import { VehicleTransfer } from '../../../../../shared/types/vehicle-transfer.types';
import { SettingListItem } from '../../../../../shared/types/setting.types';
import { FileDoc } from '../../../../../shared/types/file.types';
import useSettingLists from '../../../wrappers/use-setting-list-item';
import useVehicle from '../../../wrappers/use-vehicle';
import useVehicleService from '../../../wrappers/use-vehicle-service';
import useVehicleTransfer from '../../../wrappers/use-vehicle-transfer';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../../utils/useDateFormat';
import DocumentList from '../../../components/DocumentList';
import VehiclePicture from '../../../components/VehiclePicture';
import TransferHistory from './TransferHistory';
import {
    dealerSectionHeaderStyles,
    dealerVehicleDetailStyles,
    getDealerServiceItemStyles,
    getDealerServiceTimelineLineStyles,
    getDealerTransferTabStyles,
    getServiceActionBarStyles,
    serviceTimelineWrenchColor,
    warrantyInvalidIconColor,
    warrantyValidIconColor,
} from './styles';

const serviceStatusConfig: Record<string, { color: 'warning' | 'success' | 'error' | 'info' | 'default' }> = {
    ordered: { color: 'info' },
    inProgress: { color: 'warning' },
    waitingForParts: { color: 'warning' },
    completed: { color: 'success' },
    cancelled: { color: 'default' },
};

const VehicleDetail: React.FC = () => {
    const { vin } = useParams<{ vin: string }>();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const { user } = useAuth();
    const settingLists = useSettingLists();
    const { get: getVehicle } = useVehicle();
    const { list: listServices } = useVehicleService();
    const { list: listTransfers } = useVehicleTransfer();
    const [activeTab, setActiveTab] = useState(0);
    const [vehicle, setVehicle] = useState<Vehicle | null>(null);
    const [services, setServices] = useState<VehicleService[]>([]);
    const [transfers, setTransfers] = useState<VehicleTransfer[]>([]);
    const [serviceTypeMap, setServiceTypeMap] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        settingLists.list('serviceTypes').then((items) => {
            setServiceTypeMap(Object.fromEntries(items.map((i: SettingListItem) => [i.id, i.label])));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!vin) return;

        const fetchData = async () => {
            const loadedVehicle = await getVehicle(vin);
            if (!loadedVehicle) {
                setNotFound(true);
                setLoading(false);
                return;
            }

            setVehicle(loadedVehicle);
            const [fetchedServices, fetchedTransfers] = await Promise.all([
                user?.dealerId ? listServices({ vehicleUid: vin, dealerId: user.dealerId }) : Promise.resolve([]),
                listTransfers({ vehicleUid: vin }),
            ]);
            setServices(fetchedServices);
            setTransfers(fetchedTransfers);
            setLoading(false);
        };

        fetchData();
    }, [vin, user?.dealerId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <Box sx={dealerVehicleDetailStyles.loadingContainer}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound || !vehicle) {
        return <Alert severity="error">{t('dealerVehicleDetail.notFound', { vin })}</Alert>;
    }

    const isWarrantyValid = vehicle.warranty?.validUntil
        ? new Date(vehicle.warranty.validUntil) > new Date()
        : false;

    const lastService = services[0];

    return (
        <Box sx={dealerVehicleDetailStyles.root}>
            {/* Detail stroje */}
            <Paper elevation={0} sx={dealerVehicleDetailStyles.card}>
                <Box sx={dealerVehicleDetailStyles.detailLayout}>
                    <Box sx={dealerVehicleDetailStyles.detailMain}>
                        <Box sx={dealerSectionHeaderStyles.root}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={600}>{t('dealerVehicleDetail.detailTitle')}</Typography>
                                <Typography variant="body2" color="text.secondary" fontFamily="monospace">VIN: {vehicle.vin}</Typography>
                            </Box>
                        </Box>

                        {[
                            { label: t('dealerVehicleDetail.modelLabel'), value: vehicle.model },
                            {
                                label: t('dealerVehicleDetail.warrantyLabel'),
                                value: (
                                    <Box sx={dealerVehicleDetailStyles.inlineInfoRow}>
                                        {isWarrantyValid
                                            ? <CircleCheck size={20} color={warrantyValidIconColor} />
                                            : <ShieldX size={20} color={warrantyInvalidIconColor} />}
                                        <Typography variant="body2">
                                            {isWarrantyValid ? t('dealerVehicleDetail.warrantyValid') : t('dealerVehicleDetail.warrantyExpired')}
                                            {vehicle.warranty?.validUntil && t('dealerVehicleDetail.warrantyValidUntil', { date: formatDate(vehicle.warranty.validUntil) })}
                                        </Typography>
                                        {vehicle.warranty?.type && (
                                            <Chip label={vehicle.warranty.type} size="small" variant="outlined" />
                                        )}
                                    </Box>
                                ),
                            },
                            {
                                label: t('dealerVehicleDetail.lastServiceLabel'),
                                value: (
                                    <Box sx={dealerVehicleDetailStyles.inlineInfoRow}>
                                        <Typography variant="body2">
                                            {lastService
                                                ? formatDate(lastService.createdAt)
                                                : '—'}
                                        </Typography>
                                    </Box>
                                ),
                            },
                            vehicle.odometer && {
                                label: t('dealerVehicleDetail.odometerLabel'),
                                value: `${vehicle.odometer.kilometers} km · ${vehicle.odometer.motohours} mth`,
                            },
                        ].filter(Boolean).map((row, i, arr) => (
                            <Box key={(row as { label: string }).label}>
                                <Box sx={dealerVehicleDetailStyles.detailRow}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                        {(row as { label: string }).label}
                                    </Typography>
                                    {typeof (row as { value: unknown }).value === 'string' ? (
                                        <Typography variant="body2">{(row as { value: string }).value}</Typography>
                                    ) : (row as { value: React.ReactNode }).value}
                                </Box>
                                {i < arr.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </Box>
                    <Box sx={dealerVehicleDetailStyles.detailImageWrap}>
                        <VehiclePicture vehicle={vehicle} imgSx={{ height: 330, width: 'auto', maxWidth: 400 }} />
                    </Box>
                </Box>
            </Paper>

            {/* Záložky */}
            <Paper elevation={0} sx={dealerVehicleDetailStyles.card}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={dealerVehicleDetailStyles.tabs}>
                    <Tab label={t('dealerVehicleDetail.tabServiceHistory')} sx={dealerVehicleDetailStyles.tab} />
                    <Tab label={t('dealerVehicleDetail.tabTransfers')} sx={getDealerTransferTabStyles(transfers.length > 0)} />
                    <Tab label={t('dealerVehicleDetail.tabDocuments')} sx={dealerVehicleDetailStyles.tab} />
                </Tabs>

                {/* Tab: Historie servisu */}
                {activeTab === 0 && (
                    <>
                        <Box sx={getServiceActionBarStyles(services.length > 0)}>
                            <Button
                                variant="contained"
                                startIcon={<Wrench size={20} />}
                                component={Link}
                                to={`/app/service-desk/vehicles/${vin}/add-service`}
                            >
                                {t('dealerVehicleDetail.addService')}
                            </Button>
                        </Box>

                        {!user?.dealerId ? (
                            <Alert severity="warning">{t('dealerVehicleDetail.missingDealerId')}</Alert>
                        ) : services.length === 0 ? (
                            <Box sx={dealerVehicleDetailStyles.emptyServices}>
                                <Typography variant="body2" color="text.secondary">{t('dealerVehicleDetail.noServices')}</Typography>
                            </Box>
                        ) : (
                            <Box sx={dealerVehicleDetailStyles.servicesTimeline}>
                                {services.map((service, idx) => (
                                    <Box key={service.id} sx={getDealerServiceItemStyles(idx < services.length - 1)}>
                                        <Box sx={dealerVehicleDetailStyles.timelineColumn}>
                                            <Box sx={dealerVehicleDetailStyles.timelineDot}>
                                                <Wrench size={16} color={serviceTimelineWrenchColor} />
                                            </Box>
                                            {idx < services.length - 1 && (
                                                <Box sx={getDealerServiceTimelineLineStyles()} />
                                            )}
                                        </Box>
                                        <Box
                                            component={Link}
                                            to={`/app/service-desk/services/${service.id}`}
                                            sx={dealerVehicleDetailStyles.serviceLink}
                                        >
                                            <Box sx={dealerVehicleDetailStyles.serviceHeaderRow}>
                                                <Typography variant="body2">
                                                    <Box component="span" color="text.secondary">{serviceTypeMap[service.serviceType] ?? service.serviceType} — </Box>
                                                    {service.description || '—'}
                                                </Typography>
                                                <Box sx={dealerVehicleDetailStyles.serviceMeta}>
                                                    <Chip
                                                        label={t(`vehicleServiceStatus.${service.status}`, { defaultValue: service.status })}
                                                        color={serviceStatusConfig[service.status]?.color ?? 'default'}
                                                        size="small"
                                                    />
                                                    <Typography variant="body2" color="text.secondary" sx={dealerVehicleDetailStyles.serviceDate}>
                                                        {formatDate(service.createdAt)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {(service.currentMileage !== undefined || service.currentHours !== undefined) && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {service.currentMileage !== undefined && `${service.currentMileage} km`}
                                                    {service.currentMileage !== undefined && service.currentHours !== undefined && ' · '}
                                                    {service.currentHours !== undefined && `${service.currentHours} mth`}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </>
                )}

                {/* Tab: Převody */}
                {activeTab === 1 && transfers.length > 0 && (
                    <Box sx={dealerVehicleDetailStyles.tabContent}>
                        <TransferHistory transfers={transfers} />
                    </Box>
                )}

                {/* Tab: Dokumenty */}
                {activeTab === 2 && (
                    <Box sx={dealerVehicleDetailStyles.tabContent}>
                        <DocumentList
                            filters={{ vehicleUid: vin, dealerId: user?.dealerId }}
                            uploadData={{ vehicleUid: vin, dealerId: user?.dealerId, garageUid: vehicle?.garageUid }}
                            groups={[
                                { label: t('dealerVehicleDetail.vehicleDocuments'), predicate: (f: FileDoc) => !f.vehicleServiceUid && !f.vehicleTransferUid },
                                ...[
                                    ...services.map((svc) => ({
                                        date: svc.createdAt,
                                        label: t('dealerVehicleDetail.serviceGroupLabel', { type: serviceTypeMap[svc.serviceType] ?? svc.serviceType, date: formatDate(svc.createdAt) }),
                                        predicate: (f: FileDoc) => f.vehicleServiceUid === svc.id,
                                    })),
                                    ...transfers.map((tr) => ({
                                        date: tr.createdAt,
                                        label: t('dealerVehicleDetail.transferGroupLabel', { date: formatDate(tr.createdAt) }),
                                        predicate: (f: FileDoc) => f.vehicleTransferUid === tr.id,
                                    })),
                                ].sort((a, b) => b.date.getTime() - a.date.getTime()).map(({ label, predicate }) => ({ label, predicate })),
                            ]}
                        />
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default VehicleDetail;
